'use client';

import DOMPurify from 'dompurify';
import { Canvg } from 'canvg';
import { imagedataToSVG, optionpresets } from 'imagetracerjs';
import { optimize } from 'svgo/browser';
import {
  analyzeTransparency,
  canvasToBlob,
  clamp,
  createCanvas,
  drawImageToCanvas,
  formatAspectRatio,
  formatFileSize,
  getCanvasImageData,
  getBaseName,
  loadRasterImage,
  sanitizeHexColor,
} from '@/lib/browser-image-tools';

export type SvgMetadata = {
  width: number | null;
  height: number | null;
  viewBox: string | null;
  aspectRatio: string;
  missingWidth: boolean;
  missingHeight: boolean;
  missingViewBox: boolean;
  warnings: string[];
};

export type SvgSource = {
  name: string;
  originalMarkup: string;
  sanitizedMarkup: string;
  metadata: SvgMetadata;
};

export type SvgColorEntry = {
  original: string;
  normalized: string;
  count: number;
  contexts: string[];
};

export type SvgBackgroundMode = 'transparent' | 'white' | 'black' | 'custom';
export type RasterToSvgVariant = 'png-to-svg' | 'jpg-to-svg';

function createSanitizer() {
  return DOMPurify;
}

function parseSvgMarkup(markup: string) {
  const parser = new DOMParser();
  const document = parser.parseFromString(markup, 'image/svg+xml');
  const parserError = document.querySelector('parsererror');

  if (parserError) {
    throw new Error('This SVG markup could not be parsed cleanly.');
  }

  const svg = document.documentElement as unknown as SVGSVGElement;

  if (!svg || svg.tagName.toLowerCase() !== 'svg') {
    throw new Error('Provide valid SVG markup with one root <svg> element.');
  }

  return { document, svg };
}

function serializeSvg(svg: SVGSVGElement) {
  return new XMLSerializer().serializeToString(svg);
}

function parseDimension(value: string | null) {
  if (!value) {
    return null;
  }

  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : null;
}

function parseViewBox(value: string | null) {
  if (!value) {
    return null;
  }

  const parts = value
    .trim()
    .split(/[,\s]+/)
    .map((entry) => Number(entry))
    .filter((entry) => Number.isFinite(entry));

  if (parts.length !== 4) {
    return null;
  }

  return {
    x: parts[0],
    y: parts[1],
    width: parts[2],
    height: parts[3],
  };
}

function roundGeometry(value: number) {
  return Number(value.toFixed(3));
}

function ensureSvgMarkup(markup: string) {
  const trimmed = markup.trim();

  if (!trimmed) {
    throw new Error('Paste or upload one SVG first.');
  }

  if (!trimmed.includes('<svg')) {
    throw new Error('Provide valid SVG markup with one root <svg> element.');
  }

  return trimmed;
}

function normalizeCssColor(value: string) {
  const canvas = createCanvas(1, 1);
  const context = canvas.getContext('2d');

  if (!context) {
    return value.trim();
  }

  try {
    context.fillStyle = '#000000';
    context.fillStyle = value;
    const normalized = String(context.fillStyle).trim().toLowerCase();
    return normalized || value.trim().toLowerCase();
  } catch {
    return value.trim().toLowerCase();
  }
}

function styleStringToMap(style: string) {
  return style
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((accumulator, entry) => {
      const [property, ...rest] = entry.split(':');

      if (!property || rest.length === 0) {
        return accumulator;
      }

      accumulator[property.trim().toLowerCase()] = rest.join(':').trim();
      return accumulator;
    }, {});
}

function styleMapToString(styleMap: Record<string, string>) {
  return Object.entries(styleMap)
    .map(([property, value]) => `${property}:${value}`)
    .join(';');
}

function collectColor(colorMap: Map<string, SvgColorEntry>, value: string, context: string) {
  const trimmed = value.trim();

  if (!trimmed || trimmed === 'none' || trimmed === 'currentColor' || trimmed.startsWith('url(')) {
    return;
  }

  const normalized = normalizeCssColor(trimmed);
  const existing = colorMap.get(normalized);

  if (existing) {
    existing.count += 1;
    if (!existing.contexts.includes(context)) {
      existing.contexts.push(context);
    }
    return;
  }

  colorMap.set(normalized, {
    original: trimmed,
    normalized,
    count: 1,
    contexts: [context],
  });
}

function getNaturalSvgSize(metadata: SvgMetadata) {
  const viewBox = parseViewBox(metadata.viewBox);
  const width = metadata.width ?? viewBox?.width ?? 512;
  const height = metadata.height ?? viewBox?.height ?? width;

  return {
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height)),
  };
}

function getBackgroundColor(mode: SvgBackgroundMode, customColor: string) {
  if (mode === 'white') {
    return '#ffffff';
  }

  if (mode === 'black') {
    return '#000000';
  }

  if (mode === 'custom') {
    return sanitizeHexColor(customColor);
  }

  return null;
}

function appendSvgToMeasure(markup: string) {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-100000px';
  host.style.top = '-100000px';
  host.style.width = '0';
  host.style.height = '0';
  host.style.opacity = '0';
  host.style.pointerEvents = 'none';
  host.innerHTML = markup;
  document.body.append(host);
  const svg = host.querySelector('svg') as SVGSVGElement | null;

  if (!svg) {
    host.remove();
    throw new Error('The SVG preview could not be measured.');
  }

  return { host, svg };
}

function measureSvgBoundingBox(markup: string) {
  const { host, svg } = appendSvgToMeasure(markup);

  try {
    const viewBox = parseViewBox(svg.getAttribute('viewBox'));

    if (!svg.getAttribute('width') && viewBox?.width) {
      svg.setAttribute('width', String(viewBox.width));
    }

    if (!svg.getAttribute('height') && viewBox?.height) {
      svg.setAttribute('height', String(viewBox.height));
    }

    const bbox = svg.getBBox();

    return {
      x: roundGeometry(bbox.x),
      y: roundGeometry(bbox.y),
      width: roundGeometry(Math.max(1, bbox.width)),
      height: roundGeometry(Math.max(1, bbox.height)),
    };
  } finally {
    host.remove();
  }
}

function cloneNodeInto(doc: XMLDocument, node: Node) {
  return doc.importNode(node, true);
}

function normalizeRootAttributes(target: SVGSVGElement, source: SVGSVGElement) {
  Array.from(source.attributes).forEach((attribute) => {
    if (['xmlns', 'width', 'height', 'viewBox'].includes(attribute.name)) {
      return;
    }

    target.setAttribute(attribute.name, attribute.value);
  });
}

export function isSvgFile(file: File) {
  return file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
}

export async function readSvgFileAsText(file: File) {
  if (!isSvgFile(file)) {
    throw new Error('Choose one SVG file for this tool.');
  }

  return await file.text();
}

export function sanitizeSvgMarkup(markup: string) {
  const safeMarkup = ensureSvgMarkup(markup);
  const sanitizer = createSanitizer();
  const sanitized = sanitizer.sanitize(safeMarkup, {
    USE_PROFILES: { svg: true, svgFilters: true, html: false },
    FORBID_TAGS: ['script', 'foreignObject'],
  });

  return ensureSvgMarkup(String(sanitized));
}

export function inspectSvgMarkup(markup: string): SvgSource {
  const sanitizedMarkup = sanitizeSvgMarkup(markup);
  const { svg } = parseSvgMarkup(sanitizedMarkup);
  const width = parseDimension(svg.getAttribute('width'));
  const height = parseDimension(svg.getAttribute('height'));
  const viewBoxValue = svg.getAttribute('viewBox');
  const viewBox = parseViewBox(viewBoxValue);
  const derivedWidth = width ?? viewBox?.width ?? null;
  const derivedHeight = height ?? viewBox?.height ?? null;
  const warnings: string[] = [];

  if (width === null) {
    warnings.push('Width is missing or not numeric.');
  }

  if (height === null) {
    warnings.push('Height is missing or not numeric.');
  }

  if (!viewBoxValue || !viewBox) {
    warnings.push('viewBox is missing or invalid.');
  }

  return {
    name: 'inline.svg',
    originalMarkup: markup,
    sanitizedMarkup,
    metadata: {
      width,
      height,
      viewBox: viewBoxValue,
      aspectRatio:
        derivedWidth && derivedHeight ? formatAspectRatio(derivedWidth, derivedHeight) : 'Unknown',
      missingWidth: width === null,
      missingHeight: height === null,
      missingViewBox: !viewBoxValue || !viewBox,
      warnings,
    },
  };
}

export function createSvgPreviewUrl(markup: string) {
  const blob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
  return URL.createObjectURL(blob);
}

export function optimizeSvgMarkup(markup: string) {
  const sanitizedMarkup = sanitizeSvgMarkup(markup);
  const result = optimize(sanitizedMarkup, {
    multipass: true,
    js2svg: {
      pretty: false,
      indent: 0,
    },
    plugins: [
      {
        name: 'preset-default',
        params: {
          overrides: {
            removeViewBox: false,
            cleanupIds: true,
          },
        },
      } as never,
      'cleanupIds',
      'convertStyleToAttrs',
      'sortAttrs',
      'removeComments',
      'removeMetadata',
      'removeEditorsNSData',
    ],
  });

  return sanitizeSvgMarkup(result.data);
}

export async function renderSvgToPng(
  markup: string,
  options: {
    outputWidth?: number | null;
    scale?: number | null;
    backgroundMode: SvgBackgroundMode;
    customBackground: string;
    format?: 'png' | 'jpeg' | 'webp';
    quality?: number;
  },
) {
  const source = inspectSvgMarkup(markup);
  const natural = getNaturalSvgSize(source.metadata);
  const width = options.outputWidth
    ? Math.max(1, Math.round(options.outputWidth))
    : Math.max(1, Math.round(natural.width * Math.max(0.1, options.scale ?? 1)));
  const height = Math.max(1, Math.round((natural.height / Math.max(1, natural.width)) * width));
  const canvas = createCanvas(width, height);
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not prepare a canvas for this SVG.');
  }

  const format = options.format ?? 'png';
  const background = getBackgroundColor(options.backgroundMode, options.customBackground);

  if (background) {
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);
  } else if (format === 'jpeg') {
    // JPEG cannot store transparency, so a transparent SVG would flatten to
    // black. Fall back to white so the export looks correct.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
  } else {
    context.clearRect(0, 0, width, height);
  }

  const canvg = Canvg.fromString(context, source.sanitizedMarkup, {
    ignoreAnimation: true,
    ignoreDimensions: true,
  });
  await canvg.render();

  return await canvasToBlob(
    canvas,
    format === 'jpeg' ? 'image/jpeg' : format === 'webp' ? 'image/webp' : 'image/png',
    format === 'png' ? undefined : (options.quality ?? 0.92),
  );
}

export async function renderSvgToPdf(
  markup: string,
  options: {
    outputWidth?: number | null;
    scale?: number | null;
    backgroundMode: SvgBackgroundMode;
    customBackground: string;
  },
) {
  // Rasterize the SVG to a PNG first (keeps transparency), then embed it as a
  // single PDF page sized to the image. pdf-lib is imported dynamically so it
  // stays out of the static graph of anything that only needs the SVG helpers.
  const pngBlob = await renderSvgToPng(markup, { ...options, format: 'png' });
  const pngBytes = new Uint8Array(await pngBlob.arrayBuffer());
  const { PDFDocument } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  const image = await pdf.embedPng(pngBytes);
  const page = pdf.addPage([image.width, image.height]);
  page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
  const bytes = await pdf.save();
  return new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
}

export function detectSvgColors(markup: string) {
  const source = inspectSvgMarkup(markup);
  const { svg } = parseSvgMarkup(source.sanitizedMarkup);
  const colorMap = new Map<string, SvgColorEntry>();

  svg.querySelectorAll('*').forEach((element) => {
    ['fill', 'stroke', 'stop-color'].forEach((attribute) => {
      const value = element.getAttribute(attribute);

      if (value) {
        collectColor(colorMap, value, attribute);
      }
    });

    const style = element.getAttribute('style');

    if (style) {
      const styleMap = styleStringToMap(style);
      ['fill', 'stroke', 'stop-color'].forEach((property) => {
        if (styleMap[property]) {
          collectColor(colorMap, styleMap[property], `style:${property}`);
        }
      });
    }
  });

  return Array.from(colorMap.values()).sort((left, right) => right.count - left.count);
}

export function replaceSvgColors(markup: string, replacements: Record<string, string>) {
  const source = inspectSvgMarkup(markup);
  const { svg } = parseSvgMarkup(source.sanitizedMarkup);

  svg.querySelectorAll('*').forEach((element) => {
    ['fill', 'stroke', 'stop-color'].forEach((attribute) => {
      const value = element.getAttribute(attribute);
      const normalized = value ? normalizeCssColor(value) : null;

      if (normalized && replacements[normalized]) {
        element.setAttribute(attribute, sanitizeHexColor(replacements[normalized]));
      }
    });

    const style = element.getAttribute('style');

    if (style) {
      const styleMap = styleStringToMap(style);
      let touched = false;

      ['fill', 'stroke', 'stop-color'].forEach((property) => {
        const value = styleMap[property];
        const normalized = value ? normalizeCssColor(value) : null;

        if (normalized && replacements[normalized]) {
          styleMap[property] = sanitizeHexColor(replacements[normalized]);
          touched = true;
        }
      });

      if (touched) {
        element.setAttribute('style', styleMapToString(styleMap));
      }
    }
  });

  return sanitizeSvgMarkup(serializeSvg(svg));
}

export function cleanSvgViewBox(markup: string) {
  const source = inspectSvgMarkup(markup);
  const { svg } = parseSvgMarkup(source.sanitizedMarkup);
  const bbox = measureSvgBoundingBox(source.sanitizedMarkup);

  svg.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);

  if (!svg.getAttribute('width')) {
    svg.setAttribute('width', String(bbox.width));
  }

  if (!svg.getAttribute('height')) {
    svg.setAttribute('height', String(bbox.height));
  }

  return sanitizeSvgMarkup(serializeSvg(svg));
}

export function createSvgDataUrl(markup: string) {
  const optimized = optimizeSvgMarkup(markup);
  const encoded = encodeURIComponent(optimized)
    .replace(/%20/g, ' ')
    .replace(/%3D/g, '=')
    .replace(/%3A/g, ':')
    .replace(/%2F/g, '/')
    .replace(/%22/g, "'");

  return {
    optimizedMarkup: optimized,
    dataUrl: `data:image/svg+xml,${encoded}`,
    cssSnippet: `background-image: url("data:image/svg+xml,${encoded}");`,
  };
}

export function cleanInlineSvgMarkup(markup: string) {
  const optimized = optimizeSvgMarkup(markup);
  const { svg } = parseSvgMarkup(optimized);

  svg.removeAttribute('width');
  svg.removeAttribute('height');

  return sanitizeSvgMarkup(serializeSvg(svg));
}

export function normalizeSvgIconMarkup(
  markup: string,
  options: {
    gridSize: 16 | 20 | 24 | 32;
    trimEmptySpace: boolean;
    centerMode: 'center' | 'top-left';
  },
) {
  const source = inspectSvgMarkup(markup);
  const cleanedMarkup = options.trimEmptySpace
    ? cleanSvgViewBox(source.sanitizedMarkup)
    : source.sanitizedMarkup;
  const bbox = measureSvgBoundingBox(cleanedMarkup);
  const { svg: sourceSvg } = parseSvgMarkup(cleanedMarkup);
  const parser = new DOMParser();
  const outputDoc = parser.parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${options.gridSize} ${options.gridSize}" width="${options.gridSize}" height="${options.gridSize}"></svg>`,
    'image/svg+xml',
  );
  const outputSvg = outputDoc.documentElement as unknown as SVGSVGElement;
  normalizeRootAttributes(outputSvg, sourceSvg);

  const defsNodes: Node[] = [];
  const drawableNodes: Node[] = [];

  Array.from(sourceSvg.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'defs') {
      defsNodes.push(node);
    } else {
      drawableNodes.push(node);
    }
  });

  defsNodes.forEach((node) => outputSvg.append(cloneNodeInto(outputDoc, node)));

  const group = outputDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
  const scale = Math.min(
    options.gridSize / Math.max(1, bbox.width),
    options.gridSize / Math.max(1, bbox.height),
  );
  const translateX =
    options.centerMode === 'center'
      ? (options.gridSize - bbox.width * scale) / 2 - bbox.x * scale
      : -bbox.x * scale;
  const translateY =
    options.centerMode === 'center'
      ? (options.gridSize - bbox.height * scale) / 2 - bbox.y * scale
      : -bbox.y * scale;

  group.setAttribute(
    'transform',
    `translate(${roundGeometry(translateX)} ${roundGeometry(translateY)}) scale(${roundGeometry(scale)})`,
  );

  drawableNodes.forEach((node) => group.append(cloneNodeInto(outputDoc, node)));
  outputSvg.append(group);

  return sanitizeSvgMarkup(serializeSvg(outputSvg));
}

export async function traceRasterToSvg(params: {
  file: File;
  variant: RasterToSvgVariant;
  threshold: number;
  colorCountHint: number;
  detailPreset: 'smooth' | 'balanced' | 'detailed';
  grayscale?: boolean;
}) {
  const loaded = await loadRasterImage(params.file);
  const { context } = drawImageToCanvas(loaded.element, loaded.width, loaded.height);
  const imageData = context.getImageData(0, 0, loaded.width, loaded.height);
  const pixels = new Uint8ClampedArray(imageData.data);
  const safeThreshold = clamp(Math.round(params.threshold), 0, 255);

  for (let index = 0; index < pixels.length; index += 4) {
    const alpha = pixels[index + 3] / 255;
    const luminance = Math.round(
      pixels[index] * 0.299 + pixels[index + 1] * 0.587 + pixels[index + 2] * 0.114,
    );

    if (params.variant === 'jpg-to-svg' || params.grayscale) {
      const mono = luminance >= safeThreshold ? 255 : 0;
      pixels[index] = mono;
      pixels[index + 1] = mono;
      pixels[index + 2] = mono;
      pixels[index + 3] = Math.round(255 * alpha);
      continue;
    }

    pixels[index] = pixels[index] >= safeThreshold ? 255 : 0;
    pixels[index + 1] = pixels[index + 1] >= safeThreshold ? 255 : 0;
    pixels[index + 2] = pixels[index + 2] >= safeThreshold ? 255 : 0;
    pixels[index + 3] = Math.round(255 * alpha);
  }

  const preparedImageData = new ImageData(pixels, imageData.width, imageData.height);
  const preset =
    params.detailPreset === 'smooth'
      ? optionpresets.smoothed
      : params.detailPreset === 'detailed'
        ? optionpresets.detailed
        : optionpresets.default;
  const tracedSvg = imagedataToSVG(preparedImageData, {
    ...preset,
    pathomit: params.detailPreset === 'detailed' ? 0 : params.detailPreset === 'smooth' ? 12 : 6,
    numberofcolors: clamp(Math.round(params.colorCountHint), 2, 32),
    colorsampling: params.variant === 'jpg-to-svg' ? 0 : 2,
    linefilter: params.detailPreset === 'smooth',
    viewbox: true,
    scale: 1,
  });

  const optimized = optimizeSvgMarkup(tracedSvg);
  const previewUrl = createSvgPreviewUrl(optimized);
  const previewPng = await renderSvgToPng(optimized, {
    backgroundMode: 'white',
    customBackground: '#ffffff',
    scale: 1,
  });

  return {
    svgMarkup: optimized,
    svgBlob: new Blob([optimized], { type: 'image/svg+xml;charset=utf-8' }),
    previewUrl,
    previewPng,
    transparency: analyzeTransparency(preparedImageData.data),
  };
}

export async function copySvgText(text: string) {
  await navigator.clipboard.writeText(text);
}

export function createTextDownloadBlob(text: string, mimeType = 'text/plain;charset=utf-8') {
  return new Blob([text], { type: mimeType });
}

export { formatFileSize, getBaseName };
